import React, { useState, useEffect, useRef, useCallback } from 'react';
import './SectorIndicesWidget.css';
import { getFlagSvg } from '../../Components/Common/Flag/Flag';
import CommonTooltip from '../../Components/Common/CommonTooltip/CommonTooltip';
import {
  BaseWidgetProps,
  CommonTooltipType,
  WidgetHeightMessage,
} from '../../types/common.type';
import { formatNumber, formatKMBT, getCssVariable } from '../../utils/utils';

import { getSectorPeerCompare } from '../../api/company.api';
import {
  GetSectorIndicesResponseType,
  SectorIndicesDataType,
  SectorData,
} from '../../types/indices.type';
import { getSectorIndices } from '../../api/quote.api';
import { ReactComponent as SortIcon } from '../../assets/icons/arrow-down-s-fill.svg';
import { ReactComponent as SortAscIcon } from '../../assets/icons/arrow-up-s-fill.svg';
import { ReactComponent as SortableIcon } from '../../assets/icons/arrow-down-s-unfill.svg';
import {
  Region,
  TimePeriod,
  ThemeType,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';

import Loading from '../../Components/Loading/Loading';
import { ReactComponent as ErrorIcon } from '../../assets/icons/error-state.svg';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { errorMsg } from '../../constants/common.constant';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import useContainerWidth from '../../hooks/useContainerWidth';
import Layout from '../../Components/Common/Layout/Layout';

export interface SectorIndicesWidgetProps extends BaseWidgetProps {
  token?: string;
  region?: Region;
  period?: TimePeriod; //1D, 1M, 3M, 6M, 1YR
  select?: string;
  onCallback?: (message: SectorData) => void;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

const SectorIndicesWidget = (props: SectorIndicesWidgetProps) => {
  const { onHeightChange } = props;
  const [isEmptyData, setIsEmptyData] = useState(false);
  const msgItem = errorMsg.find((obj) => obj.key === 'indexList');

  const sortcolMap = {
    '1D': 'perf1D',
    '1M': 'perf1M',
    '3M': 'perf3M',
    '6M': 'perf6M',
    '1YR': 'perf1Y',
  };
  const widgetRef = useRef<HTMLDivElement | null>(null);
  useResizeNotifier(widgetRef, WidgetNameSource.SectorIndices, onHeightChange);
  const { isSmallScreen } = useContainerWidth(widgetRef);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const onLoadingChangeRef = useRef(props.onLoadingChange);
  onLoadingChangeRef.current = props.onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  const [select, setSelect] = useState<string>(props.select || '');
  const [data, setData] = useState<SectorIndicesDataType[]>([]);
  const [sortcol, setSortcol] = useState<string>(
    sortcolMap[props.period as keyof typeof sortcolMap] || 'perf3M',
  );
  const [sortord, setSortord] = useState<string>('desc');

  const [tooltip, setTooltip] = useState<CommonTooltipType>({
    content: '',
    show: false,
    x: 0,
    y: 0,
  });

  // ToolTip
  const peercomTooltipRef = useRef<HTMLDivElement | null>(null);
  const constrainTooltipPosition = (e: React.MouseEvent) => {
    // Estimate tooltip dimensions (these are approximate values from CSS)
    // Calculate initial position based on item position and mouse offset
    let x = e.pageX + 10;
    let y = e.pageY;

    const elementMaxWidth = peercomTooltipRef.current?.clientWidth || 0;
    const elementMaxHeight = peercomTooltipRef.current?.clientHeight || 0;
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    const viewportWidth = window.innerWidth + scrollX;
    const viewportHeight = window.innerHeight + scrollY;

    if (x + elementMaxWidth > viewportWidth) {
      x = x - elementMaxWidth;
    }
    if (y + elementMaxHeight > viewportHeight) {
      y = y - elementMaxHeight;
    }

    return { x, y };
  };

  function setToolTips(item: any, fid: string): any {
    return (
      <div className="sec-ind-tbl-cel-flex-container">
        <span
          className="sec-ind-tbl-cel-text"
          onMouseEnter={(e) => {
            const constrainedPosition = constrainTooltipPosition(e);
            setTooltip({
              show: true,
              x: constrainedPosition.x,
              y: constrainedPosition.y,
              content: item.name,
            });
          }}
          onMouseMove={(e) => {
            //if no move, do not update tooltip
            if (e.movementX === 0 && e.movementY === 0) {
              return;
            }
            const constrainedPosition = constrainTooltipPosition(e);
            setTooltip((prev) => ({
              ...prev,
              x: constrainedPosition.x,
              y: constrainedPosition.y,
            }));
          }}
          onMouseLeave={() => {
            setTooltip((prev) => ({ ...prev, show: false }));
          }}
          onMouseOut={() => {
            setTooltip((prev) => ({ ...prev, show: false }));
          }}
        >
          {item.name}
        </span>
      </div>
    );
  }

  useEffect(() => {
    if (props.select) {
      setSelect(props.select);
    }
  }, [props.select]);

  useEffect(() => {}, [select]);

  // Initial state for reset functionality
  const initialColor = [
    {
      color: getCssVariable('--heatmap-up-6'),
      fontColor: getCssVariable('--text-invert'),
      min: 5,
      max: 9999.0,
      label: '+5%',
    },
    {
      color: getCssVariable('--heatmap-up-5'),
      fontColor: getCssVariable('--text-invert'),
      min: 4,
      max: 5,
      label: '+4%',
    },
    {
      color: getCssVariable('--heatmap-up-4'),
      fontColor: getCssVariable('--text-invert'),
      min: 3,
      max: 4,
      label: '+3%',
    },
    {
      color: getCssVariable('--heatmap-up-3'),
      fontColor: getCssVariable('--text-invert'),
      min: 2,
      max: 3,
      label: '+2%',
    },
    {
      color: getCssVariable('--heatmap-up-2'),
      fontColor: getCssVariable('--text-primary'),
      min: 1,
      max: 2,
      label: '+1%',
    }, // Bổ sung dải +1% để đối xứng với -1%
    {
      color: getCssVariable('--heatmap-up-1'),
      fontColor: getCssVariable('--text-primary'),
      min: 0.5,
      max: 1,
      label: '+0.5%',
    },
    {
      color: getCssVariable('--heatmap-no-chg'),
      fontColor: getCssVariable('--text-unchg-color'),
      min: -0.5,
      max: 0.5,
      label: '0%',
    },
    {
      color: getCssVariable('--heatmap-down-1'),
      fontColor: getCssVariable('--text-primary'),
      min: -1,
      max: -0.5,
      label: '-0.5%',
    },
    {
      color: getCssVariable('--heatmap-down-2'),
      fontColor: getCssVariable('--text-primary'),
      min: -2,
      max: -1,
      label: '-1%',
    },
    {
      color: getCssVariable('--heatmap-down-3'),
      fontColor: getCssVariable('--text-invert'),
      min: -3,
      max: -2,
      label: '-2%',
    },
    {
      color: getCssVariable('--heatmap-down-4'),
      fontColor: getCssVariable('--text-invert'),
      min: -4,
      max: -3,
      label: '-3%',
    },
    {
      color: getCssVariable('--heatmap-down-5'),
      fontColor: getCssVariable('--text-invert'),
      min: -5,
      max: -4,
      label: '-4%',
    },
    {
      color: getCssVariable('--heatmap-down-6'),
      fontColor: getCssVariable('--text-invert'),
      min: -9999.0,
      max: -5,
      label: '-5%',
    },
  ];

  // Call API when page changes or sort changes
  useEffect(() => {
    //console.log('useEffect', props.region);
    fetchSectorIndicesData();
  }, [props.token, props.region, sortcol, sortord]);

  useEffect(() => {
    //if props.period is 1D, 1M, 3M, 6M, 1YR, set sortcol to perf1D, perf1M, perf3M, perf6M, perf1Y
    setSortcol(sortcolMap[props.period as keyof typeof sortcolMap] || 'perf3M');
  }, [props.period]);

  const fetchSectorIndicesData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    //console.log('fetchSectorIndicesData', props.region, sortcol, sortord);
    try {
      const res: GetSectorIndicesResponseType = await getSectorIndices(
        props.region || '',
        props.token || '',
        sortcol,
        sortord,
      );
      if (res) {
        const arr: SectorIndicesDataType[] = [];
        for (const key in res) {
          if (res[key]) {
            //if perf1D and perf1M and perf3M and perf6M and perf1Y no data, add * at name end.
            if (
              !res[key].perf1D &&
              !res[key].perf1M &&
              !res[key].perf3M &&
              !res[key].perf6M &&
              !res[key].perf1Y
            ) {
              res[key].name = res[key].name + '**';
            }
            arr.push(res[key]);
          }
        }
        setData(arr);
        setIsEmptyData(arr.length === 0);
        if (arr.length === 0) {
          props.onNoData?.({
            type: WidgetMessageType.NoData,
            source: 'SectorIndicesWidget',
            data: null,
          });
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch sector indices data');
      console.error('Error fetching recommendation data:', err);
      props.onError?.({
        message: err.message,
        type: err.name + '_' + err.code,
      });
      setIsEmptyData(true);
    } finally {
      setIsLoading(false);
    }
  }, [props.token, props.region, sortcol, sortord]);

  // Get Broker Rating Class
  const getRecommendationClass = (recommendation: string) => {
    switch (recommendation) {
      case 'Strong Buy':
        return 'rec-strong-buy';
      case 'Buy':
        return 'rec-buy';
      case 'Hold':
        return 'rec-hold';
      case 'Reduce':
        return 'rec-reduce';
      case 'Sell':
        return 'rec-sell';
      default:
        return 'rec-hold';
    }
  };

  function getCompanyScoreClass(value: number): string {
    let scoreClass = '';
    if (value) {
      switch (value) {
        case 10:
          scoreClass = 'score-10';
          break;
        default:
          scoreClass = 'score';
      }
    }

    return scoreClass;
  }

  function getUpDownClass(value: number): string {
    let updownclass = '';
    if (value) {
      if (value > 0) updownclass = 'positive';
      else if (value < 0) updownclass = 'negative';
      else if ((value = 0)) updownclass = 'nochange';
    }

    return updownclass;
  }

  function getHeatmapStyle(value: number): any {
    if (!value) {
      return {
        backgroundColor: getCssVariable('--heatmap-no-chg'),
        color: getCssVariable('--text-secondary'),
      };
    }
    for (const range of initialColor) {
      if (value >= range.min && value < range.max) {
        return { backgroundColor: range.color, color: range.fontColor };
      }
    }
  }

  function cutOffWords(text: string): string {
    const maxChars = isSmallScreen ? 50 : 22;
    const isLong = text.length > maxChars;
    const shown = !isLong ? text : text.slice(0, maxChars) + '…';
    return shown;
  }

  function handleOnClick(sector: string) {
    setSelect(sector);
    if (props.onCallback && sector) {
      let message = {
        data: { sector: sector },
        source: 'sectorindices',
        type: WidgetMessageType.Sector,
      };

      if ((window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify(message),
          '*',
        );
      }

      window.parent.postMessage(JSON.stringify(message), '*');
      props.onCallback(message);
    }
  }

  function handleSort(column: string) {
    if (sortcol === column) {
      // Toggle sort order if clicking the same column
      setSortord(sortord === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to desc
      setSortcol(column);
      setSortord('desc');
    }
  }

  function getSortIcon(column: string): React.ReactNode {
    const isActive = sortcol === column;
    const Icon = isActive
      ? sortord === 'asc'
        ? SortAscIcon
        : SortIcon
      : SortableIcon;
    return (
      <Icon
        className={
          isActive
            ? sortord === 'asc'
              ? 'sort-icon-asc'
              : 'sort-icon-desc'
            : 'sort-icon-sortable'
        }
      />
    );
  }

  const smallScreenPeriodLabelMap: Record<string, string> = {
    perf1D: '1D Performance',
    perf1M: '1M Performance',
    perf3M: '3M Performance',
    perf6M: '6M Performance',
    perf1Y: '1Y Performance',
  };
  const smallScreenPeriodKey = (sortcolMap[
    props.period as keyof typeof sortcolMap
  ] || 'perf3M') as 'perf1D' | 'perf1M' | 'perf3M' | 'perf6M' | 'perf1Y';
  const smallScreenPeriodLabel =
    smallScreenPeriodLabelMap[smallScreenPeriodKey];

  return (
    <Layout theme={props?.theme || ThemeType.Direct}>
      <div ref={widgetRef} className="sec-ind-container">
        {isLoading && !props.onLoadingChange ? (
          <Loading />
        ) : !isLoading && isEmptyData ? (
          <ErrorMessage msg={msgItem} />
        ) : (
          <div>
            {isSmallScreen ? (
              <div className="sec-ind-list">
                <div className="sec-ind-list-header">
                  <div
                    className="sec-ind-list-name sortable"
                    onClick={() => handleSort('name')}
                  >
                    Sector {getSortIcon('name')}
                  </div>
                  <div
                    className="sec-ind-list-perf sortable"
                    onClick={() => handleSort(smallScreenPeriodKey)}
                  >
                    {smallScreenPeriodLabel} {getSortIcon(smallScreenPeriodKey)}
                  </div>
                </div>
                <div className="sec-ind-list-body">
                  {data.length ? (
                    data.map((item, index) => {
                      const value = item[smallScreenPeriodKey];
                      return (
                        <div className="sec-ind-list-row" key={index}>
                          <div className="sec-ind-list-name-cell">
                            <span className="sec-ind-list-ctry">
                              {item.cc ? getFlagSvg(item.cc) : '-'}
                            </span>
                            <span
                              className={`sec-ind-list-name ${select === item.sector + '' ? 'highlighted' : ''}`}
                              onClick={() => handleOnClick(item.sector + '')}
                            >
                              {item.name ? setToolTips(item, 'name') : '-'}
                            </span>
                          </div>
                          <div
                            className={`sec-ind-list-perf ${getUpDownClass(value)}`}
                          >
                            {formatNumber(value, 2, 2, false, true, false, '%')}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="sec-ind-list-no-data">No data</div>
                  )}
                </div>
                <div className="sec-ind-list-footer">
                  * Performance data currently not available
                </div>
              </div>
            ) : (
              <table className="sec-ind-table">
                <thead>
                  <tr>
                    <th className=""></th>
                    <th className="ctry"></th>
                    <th
                      className="name sortable"
                      onClick={() => handleSort('name')}
                    >
                      Sector {getSortIcon('name')}
                    </th>
                    <th
                      className="perf1D sortable"
                      onClick={() => handleSort('perf1D')}
                    >
                      1D {getSortIcon('perf1D')}
                    </th>
                    <th
                      className="perf1M sortable"
                      onClick={() => handleSort('perf1M')}
                    >
                      1M {getSortIcon('perf1M')}
                    </th>
                    <th
                      className="perf3M sortable"
                      onClick={() => handleSort('perf3M')}
                    >
                      3M {getSortIcon('perf3M')}
                    </th>
                    <th
                      className="perf6M sortable"
                      onClick={() => handleSort('perf6M')}
                    >
                      6M {getSortIcon('perf6M')}
                    </th>
                    <th
                      className="perf1Y sortable"
                      onClick={() => handleSort('perf1Y')}
                    >
                      1Y {getSortIcon('perf1Y')}
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data ? (
                    data.map((item, index) => {
                      return (
                        <tr key={index}>
                          <td className=""></td>
                          <td className="ctry">
                            {item.cc ? getFlagSvg(item.cc) : '-'}
                          </td>
                          <td
                            className={`name ${select === item.sector + '' ? 'highlighted' : ''}`}
                            onClick={() => handleOnClick(item.sector + '')}
                          >
                            {item.name ? setToolTips(item, 'name') : '-'}
                          </td>
                          <td
                            className="perf1D"
                            style={getHeatmapStyle(item.perf1D)}
                          >
                            {formatNumber(
                              item.perf1D,
                              2,
                              2,
                              false,
                              true,
                              false,
                              '%',
                            )}
                          </td>
                          <td
                            className="perf1M"
                            style={getHeatmapStyle(item.perf1M)}
                          >
                            {formatNumber(
                              item.perf1M,
                              2,
                              2,
                              false,
                              true,
                              false,
                              '%',
                            )}
                          </td>
                          <td
                            className="perf3M"
                            style={getHeatmapStyle(item.perf3M)}
                          >
                            {formatNumber(
                              item.perf3M,
                              2,
                              2,
                              false,
                              true,
                              false,
                              '%',
                            )}
                          </td>
                          <td
                            className="perf6M"
                            style={getHeatmapStyle(item.perf6M)}
                          >
                            {formatNumber(
                              item.perf6M,
                              2,
                              2,
                              false,
                              true,
                              false,
                              '%',
                            )}
                          </td>
                          <td
                            className="perf1Y"
                            style={getHeatmapStyle(item.perf1Y)}
                          >
                            {formatNumber(
                              item.perf1Y,
                              2,
                              2,
                              false,
                              true,
                              false,
                              '%',
                            )}
                          </td>
                          <td className="lastcolumn"></td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={10} className="no-data">
                        No data
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={10}>
                      * Performance data currently not available
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}

            <div className="sec-ind-whitespace"></div>

            <CommonTooltip
              ref={peercomTooltipRef}
              content={tooltip.content}
              show={tooltip.show}
              x={tooltip.x}
              y={tooltip.y}
            />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SectorIndicesWidget;
